import { useState, useEffect } from 'react'
import { Routes, Route, Link, useSearchParams } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { getMe, listarProdutos, listarGrupos } from './api'
import type { ListarProdutosParams } from './api'
import ProdutoDetalhe from './ProdutoDetalhe'
import EquivalenciaList from './EquivalenciaList'
import EquivalenciaDetail from './EquivalenciaDetail'
import CuradoriaBusca from './CuradoriaBusca'

function useDebounce<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}

function Header() {
  const { data: me, isLoading, error } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false })

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="text-lg font-bold text-blue-900">
          Catálogo Admin
        </Link>
        <div className="text-sm">
          {isLoading && <span className="text-slate-400">…</span>}
          {error && (
            <a href="https://portal.lojasprorural.com.br/login" className="text-red-600 underline">
              Faça login no Portal
            </a>
          )}
          {me && (
            <span className="text-slate-700">
              {me.name} {me.is_admin && <span className="ml-1 text-xs text-blue-600">(admin)</span>}
            </span>
          )}
        </div>
      </div>
      <nav className="max-w-[1400px] mx-auto px-4 pb-2 flex gap-4 text-sm">
        <Link to="/" className="text-slate-600 hover:text-blue-700">Produtos</Link>
        <Link to="/equivalencia" className="text-slate-600 hover:text-blue-700">Equivalência</Link>
        <Link to="/curadoria-busca" className="text-slate-600 hover:text-blue-700">Curadoria de Busca</Link>
        <Link to="/imagens" className="text-slate-600 hover:text-blue-700">Imagens</Link>
        <Link to="/atributos" className="text-slate-600 hover:text-blue-700">Atributos</Link>
        <Link to="/grupos" className="text-slate-600 hover:text-blue-700">Grupos</Link>
      </nav>
    </header>
  )
}

const POR_PAGINA = 50

function ProdutosLista() {
  // Filtros persistidos na URL (?grupo=&enriq=&pagina=&q=&ordenar=&direcao=) para
  // sobreviverem à navegação produto → voltar e funcionarem com o botão voltar.
  const [searchParams, setSearchParams] = useSearchParams()
  const grupo = searchParams.get('grupo') ?? ''
  const enriquecimento = searchParams.get('enriq') ?? ''
  const ordenar = searchParams.get('ordenar') ?? 'atualizado_em'
  const direcao: 'asc' | 'desc' = searchParams.get('direcao') === 'asc' ? 'asc' : 'desc'
  const pagina = Math.max(1, Number(searchParams.get('pagina')) || 1)

  const [busca, setBusca] = useState(searchParams.get('q') ?? '')
  const [mostrarFiltros, setMostrarFiltros] = useState(!!enriquecimento)

  function patchParams(patch: Record<string, string | null>, replace = false) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '') next.delete(k)
        else next.set(k, v)
      }
      return next
    }, { replace })
  }

  const q = useDebounce(busca.trim(), 300)
  const pesquisando = q.length > 0

  // Espelha a busca (debounced) na URL e volta pra página 1 ao mudar filtros
  useEffect(() => {
    patchParams({ q: q || null, pagina: null }, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  const queryParams: ListarProdutosParams = {
    q: q || undefined,
    incompletos: !pesquisando && !grupo && !enriquecimento ? true : undefined,
    limit: POR_PAGINA,
    offset: (pagina - 1) * POR_PAGINA,
    grupo: grupo || undefined,
    enriquecimento: enriquecimento || undefined,
    ordenar,
    direcao,
  }

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['produtos', queryParams],
    queryFn: () => listarProdutos(queryParams),
    placeholderData: keepPreviousData,
  })

  const { data: grupos } = useQuery({
    queryKey: ['grupos-erp'],
    queryFn: listarGrupos,
    staleTime: 5 * 60_000,
  })

  const totalPaginas = data ? Math.ceil(data.total / POR_PAGINA) : 0
  const temFiltrosAtivos = grupo || enriquecimento

  function toggleOrdem(col: string) {
    if (ordenar === col) {
      patchParams({ direcao: direcao === 'asc' ? 'desc' : 'asc', pagina: null })
    } else {
      patchParams({ ordenar: col, direcao: col === 'nome' ? 'asc' : 'desc', pagina: null })
    }
  }

  function SortIcon({ col }: { col: string }) {
    if (ordenar !== col) return <span className="text-slate-300 ml-0.5">↕</span>
    return <span className="text-blue-600 ml-0.5">{direcao === 'asc' ? '↑' : '↓'}</span>
  }

  if (error && !data)
    return (
      <p className="p-8 text-red-600">
        Erro: {error instanceof Error ? error.message : String(error)}
      </p>
    )

  const titulo = pesquisando
    ? 'Busca de produtos'
    : temFiltrosAtivos
      ? 'Produtos filtrados'
      : 'Produtos pendentes de enriquecimento'

  const subtitulo = data
    ? `${data.total.toLocaleString('pt-BR')} produto${data.total !== 1 ? 's' : ''}${pesquisando ? ' encontrado' + (data.total !== 1 ? 's' : '') : temFiltrosAtivos ? '' : ' sem imagem ou descrição rica'}`
    : '…'

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{titulo}</h1>
          <p className="text-sm text-slate-500">{subtitulo}</p>
        </div>
      </div>

      {/* Busca */}
      <div className="relative mb-3">
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Pesquisar por nome ou código (SKU)…"
          className="w-full px-4 py-2.5 pl-10 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {isFetching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {/* Toggle filtros */}
      <div className="flex items-center gap-3 mb-3">
        {/* Grupo do ERP — sempre visível (filtro rápido), espelhado de prodgrupo a cada 10min.
            Árvore achatada: indentação por nível, código do ERP e total da subárvore.
            Selecionar um pai filtra também os filhos. */}
        <select
          value={grupo}
          onChange={e => patchParams({ grupo: e.target.value || null, pagina: null })}
          className={`text-sm px-2 py-1.5 rounded-md border bg-white max-w-[420px] font-mono ${grupo ? 'border-blue-300 text-blue-700' : 'border-slate-300 text-slate-600'}`}
        >
          <option value="">Todos os grupos (ERP)</option>
          {grupos?.map(g => (
            <option key={g.codigo} value={g.codigo}>
              {'    '.repeat(g.nivel)}
              {g.nivel > 0 ? '└ ' : ''}
              {g.codigo === '__sem__' ? '——————' : g.codigo} · {g.nome} ({g.total_arvore.toLocaleString('pt-BR')})
            </option>
          ))}
        </select>
        <button
          onClick={() => setMostrarFiltros(f => !f)}
          className={`text-xs px-3 py-1.5 rounded-md border ${mostrarFiltros || enriquecimento ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}
        >
          Mais filtros {enriquecimento && '(1)'}
        </button>
        {temFiltrosAtivos && (
          <button
            onClick={() => patchParams({ grupo: null, enriq: null, pagina: null })}
            className="text-xs text-red-600 hover:underline"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Painel de filtros */}
      {mostrarFiltros && (
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex flex-wrap gap-3 items-end text-sm">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Enriquecimento</label>
            <select
              value={enriquecimento}
              onChange={e => patchParams({ enriq: e.target.value || null, pagina: null })}
              className="px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
            >
              <option value="">Qualquer</option>
              <option value="com_imagem">Com imagem</option>
              <option value="sem_imagem">Sem imagem</option>
              <option value="com_meta">Com descrição rica</option>
              <option value="sem_meta">Sem descrição rica</option>
              <option value="completo">Completo (imagem + meta)</option>
            </select>
          </div>
        </div>
      )}

      {isLoading && <p className="p-8 text-slate-500">Carregando…</p>}

      {data && <>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-left px-4 py-3 cursor-pointer select-none" onClick={() => toggleOrdem('nome')}>
                  Nome <SortIcon col="nome" />
                </th>
                <th className="text-left px-4 py-3">Grupo (ERP)</th>
                <th className="text-right px-4 py-3 cursor-pointer select-none" onClick={() => toggleOrdem('estoque')}>
                  Estoque <SortIcon col="estoque" />
                </th>
                <th className="text-center px-4 py-3 cursor-pointer select-none" onClick={() => toggleOrdem('imagens')}>
                  Imagens <SortIcon col="imagens" />
                </th>
                <th className="text-center px-4 py-3">Atributos</th>
                <th className="text-center px-4 py-3">Meta</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((p) => (
                <tr key={p.codigo} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">
                    <Link to={`/produto/${encodeURIComponent(p.codigo)}`} className="text-blue-700 hover:underline">
                      {p.codigo}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-slate-800">
                    <Link to={`/produto/${encodeURIComponent(p.codigo)}`} className="hover:text-blue-700">
                      {p.nome}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs">
                    {p.grupo_nome ? <><span className="font-mono text-slate-400">{p.grupo}</span> · {p.grupo_nome}</> : '—'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{p.estoque}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={p.qtd_imagens > 0 ? 'text-green-600' : 'text-slate-300'}>
                      {p.qtd_imagens}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={p.qtd_atributos > 0 ? 'text-green-600' : 'text-slate-300'}>
                      {p.qtd_atributos}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {p.tem_meta ? <span className="text-green-600">✓</span> : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
              {data.data.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Nenhum produto encontrado</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPaginas > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-slate-500">
              {((pagina - 1) * POR_PAGINA + 1).toLocaleString('pt-BR')}–{Math.min(pagina * POR_PAGINA, data.total).toLocaleString('pt-BR')} de {data.total.toLocaleString('pt-BR')}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => patchParams({ pagina: null })}
                disabled={pagina === 1}
                className="px-2 py-1 text-xs rounded border border-slate-300 disabled:opacity-30 hover:bg-slate-50"
              >
                ««
              </button>
              <button
                onClick={() => patchParams({ pagina: pagina - 1 > 1 ? String(pagina - 1) : null })}
                disabled={pagina === 1}
                className="px-2 py-1 text-xs rounded border border-slate-300 disabled:opacity-30 hover:bg-slate-50"
              >
                «
              </button>
              {paginasVisiveis(pagina, totalPaginas).map((n, i) =>
                n === null ? (
                  <span key={`e${i}`} className="px-1 text-xs text-slate-400">…</span>
                ) : (
                  <button
                    key={n}
                    onClick={() => patchParams({ pagina: n > 1 ? String(n) : null })}
                    className={`px-2.5 py-1 text-xs rounded border ${n === pagina ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 hover:bg-slate-50'}`}
                  >
                    {n}
                  </button>
                ),
              )}
              <button
                onClick={() => patchParams({ pagina: String(Math.min(totalPaginas, pagina + 1)) })}
                disabled={pagina === totalPaginas}
                className="px-2 py-1 text-xs rounded border border-slate-300 disabled:opacity-30 hover:bg-slate-50"
              >
                »
              </button>
              <button
                onClick={() => patchParams({ pagina: String(totalPaginas) })}
                disabled={pagina === totalPaginas}
                className="px-2 py-1 text-xs rounded border border-slate-300 disabled:opacity-30 hover:bg-slate-50"
              >
                »»
              </button>
            </div>
          </div>
        )}
      </>}
    </div>
  )
}

/** Gera array de números de página visíveis com elipses */
function paginasVisiveis(atual: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages: (number | null)[] = []
  pages.push(1)
  if (atual > 3) pages.push(null)
  for (let i = Math.max(2, atual - 1); i <= Math.min(total - 1, atual + 1); i++) {
    pages.push(i)
  }
  if (atual < total - 2) pages.push(null)
  pages.push(total)
  return pages
}

function Placeholder({ titulo }: { titulo: string }) {
  return (
    <div className="max-w-[1400px] mx-auto px-4 py-12 text-center text-slate-500">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">{titulo}</h1>
      <p>Em construção. Próximas iterações.</p>
    </div>
  )
}

export default function App() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<ProdutosLista />} />
          <Route path="/produto/:codigo" element={<ProdutoDetalhe />} />
          <Route path="/equivalencia" element={<EquivalenciaList />} />
          <Route path="/equivalencia/:id" element={<EquivalenciaDetail />} />
          <Route path="/curadoria-busca" element={<CuradoriaBusca />} />
          <Route path="/imagens" element={<Placeholder titulo="Imagens" />} />
          <Route path="/atributos" element={<Placeholder titulo="Atributos" />} />
          <Route path="/grupos" element={<Placeholder titulo="Grupos do ERP" />} />
        </Routes>
      </main>
    </div>
  )
}
